/**
 * `SkillsList` — extracted from the prior `SkillsStep` body. Per
 * ADR-0042 §3 this renders as a sibling of `InstallMethodTabs` inside
 * `InstallAndSkillsStep`, NOT as a child of the tab panels — that way
 * switching tabs does NOT unmount the skill toggles or the suggestion
 * panel (the suggestion is per-site, not per-tab).
 *
 * Props evolved from `SkillsStepProps`:
 *   - `activeInstallMethod` is accepted for forward-compatibility with
 *     ADR-0044 (custom-skill rows / Shopify wired-by-adapter highlighting);
 *     Slice 1 shows the same default-list regardless of method.
 *   - `extraction` is the `UseProfileExtractionState` from the parent —
 *     surfaced for the in-flight `running` / `failed` status copy. The
 *     actual rendered suggestion rows are passed in via
 *     `recommendations` (ADR-0050 §2.3 — detection-driven baseline
 *     composed with the dormant ADR-0040 LLM enrichment).
 *   - `recommendations` (ADR-0050) — the merged `SkillRecommendation[]`
 *     baseline. Empty means "no panel"; non-empty drives the
 *     suggestion surface. Each row's `source` selects the badge
 *     (`'llm'` → "SILL ANALYZER"; `'detection'` → "DETECTED").
 *
 * Security-critical invariants preserved verbatim from `SkillsStep`:
 *   - Closed-enum guard (`isClosedSkillId`) at the suggestion-render
 *     boundary AND the Apply-handler boundary (ADR-0040 §3.2 defense 2).
 *   - Rationale rendered as plain React text (auto-escaped) with a
 *     defensive 280-codepoint cap (ADR-0040 §3.2 defense 7).
 *   - Apply routes through `api.skills.saveForSite` (the existing typed
 *     toggle path), NOT any suggestion-bag method (ADR-0040 §4.4).
 *
 * Two accuracy fixes (2026-07-09, founder report on vbout.com — a
 * cart-free WordPress site showing all seven commerce skills,
 * including place_order/request_refund, as "enabled"):
 *
 *   1. A manual `Toggle` click in the main grid previously only
 *      updated LOCAL React state — `saveForSite` was never called,
 *      so unchecking a skill during onboarding was silently
 *      discarded (a prior test explicitly pinned this as
 *      intentional, misreading it as the same "no-auto-apply" floor
 *      that governs suggestion rows — that floor is about not
 *      auto-applying a SYSTEM suggestion without a click; a direct
 *      merchant click on the primary Toggle already IS the click).
 *      `toggleSkill` now persists, same path as `applySuggested`.
 *
 *   2. The initial `enabled` state came from the platform-agnostic
 *      `DEFAULT_SKILLS` template (all seven `true`, always) even
 *      though the intro copy claims "we pre-selected a starting set
 *      from what we saw on your site." Nothing about that set was
 *      ever derived from the site. On the FIRST successful, non-empty
 *      `recommendations` result (detection-only baseline, not the
 *      LLM), the grid now narrows to exactly the recommended subset —
 *      once, before any manual touch, and persisted immediately so
 *      the honest-once-narrowed default a Chrome refresh right after
 *      would also see. A merchant's own click always wins: once
 *      `touchedRef` flips, detection is never allowed to narrow the
 *      grid again.
 *
 * Third accuracy fix (2026-07-27, founder re-report on vbout.com — a
 * non-store site was shown all seven skills, including place_order /
 * request_refund, as active suggestions):
 *
 *   3. (2026-07-28, vbout.com again — a non-store site was still shown
 *      order_status / track_shipment / browse as active suggestions.)
 *      NOTHING is pre-selected. The grid starts ALL OFF and skills only
 *      light up via the detection-narrow effect when detection actually
 *      recommends them (a store/CMS with a catalog). An undetected or
 *      non-commerce site shows ZERO suggested skills — the merchant
 *      enables exactly what they want. Transactional skills additionally
 *      stay locked until a payment path is connected (enforced in the
 *      narrow effect + the amber-Lock affordance). The intro copy
 *      (`SkillsStep`) reflects the no-detection case ("we didn't detect a
 *      store, so nothing's pre-selected").
 */

import { motion } from 'framer-motion';
import {
  Check,
  Eye,
  Info,
  Lock,
  Package,
  Receipt,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  Truck,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import type { Skill, SkillId } from '@sill/types';
import { DEFAULT_SKILLS } from '@sill/types';

import { useApi } from '../../api/api-context.js';
import { useSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { Pill, Toggle, Tooltip } from '../../components/atoms/index.js';
import type { UseProfileExtractionState } from '../../hooks/useProfileExtraction.js';
import type { SkillRecommendation } from '../../lib/skill-recommendations.js';
import { SkillPreviewModal } from './SkillPreviewModal.js';
import type { InstallMethod } from './InstallMethodTabs.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

type IconType = LucideIcon;

const ICONS: Record<string, IconType> = {
  browse_catalog: Package,
  check_availability: Eye,
  place_order: ShoppingBag,
  order_status: Receipt,
  track_shipment: Truck,
  request_refund: RefreshCw,
  recommend: Sparkles,
};

function iconFor(skill_id: SkillId): IconType {
  return ICONS[skill_id] ?? Sparkles;
}

/**
 * Human, UNIQUE per-skill explanation (founder 2026-07-15: the old hover
 * tooltip showed the SAME platform-level scan rationale on every card —
 * "vague and useless as it is the same for ALL"). We render this CURATED
 * STATIC copy, NOT the untrusted scan/LLM rationale — which also closes
 * the ADR-0040 §3.2 defense-7 render surface by construction (no
 * attacker-controllable string reaches the DOM at all).
 */
const SKILL_TOOLTIPS: Record<string, string> = {
  browse_catalog:
    'Lets an agent search and browse your products. Works as soon as your catalog is discoverable — no sign-in, no payment.',
  check_availability:
    'Lets an agent check live stock and shipping ETA before it recommends or buys. Read-only.',
  place_order:
    "Turning this on makes it configurable — an agent still can't place a real order until you connect a payment path (Stripe, or the payment-link handoff). Until then it stays inert, with no silent failures.",
  order_status:
    "Lets an agent look up where an order stands, only with the buyer's signature.",
  track_shipment:
    'Lets an agent return carrier tracking for an order, scoped to the authenticated buyer.',
  request_refund:
    "Lets an agent start a refund or return on the buyer's behalf. Signed, and settles over your payment rail once connected.",
  recommend:
    "Lets an agent suggest related products from a shopper's prompt. Read-only — no checkout.",
};

function tooltipFor(skill_id: SkillId): string {
  return (
    SKILL_TOOLTIPS[skill_id]
    ?? 'Lets an agent use this capability once its backing is connected.'
  );
}

/** Skills that cannot actually execute until a payment path is connected.
 *  They render a NEEDS PAYMENT badge + an honest hint when OFF so a
 *  merchant never assumes toggling alone enables a real charge
 *  (VERIFIED_FACTS: live checkout is Stripe-only + dogfood; the Woo
 *  connector is TBD). */
const NEEDS_PAYMENT_SKILLS: ReadonlySet<string> = new Set([
  'place_order',
  'request_refund',
]);

/**
 * Canonical closed 7-element skill_id allow-list. Sourced from
 * `DEFAULT_SKILLS`; the server-side post-validator already enforces
 * the enum (ADR-0040 §3.2 defense 2), this is defense-in-depth at the
 * mutation boundary so an out-of-enum suggestion can never trigger a
 * toggle on a row that isn't in the default list.
 */
const CLOSED_SKILL_ID_SET: ReadonlySet<string> = new Set<string>(
  DEFAULT_SKILLS.map((s) => s.skill_id),
);

function isClosedSkillId(value: unknown): value is SkillId {
  return typeof value === 'string' && CLOSED_SKILL_ID_SET.has(value);
}

export interface SkillsListProps {
  /** The active install-method tab — forward-compatible with ADR-0044
   *  (Slice 1 surfaces the same default-list for every method; only
   *  the `shopify` tab labels apply as "wired by Shopify adapter" in
   *  future slices). Optional after ADR-0049 — the Skills step (step
   *  1) renders BEFORE the merchant picks an install method, so the
   *  prop is omitted there. When omitted the list is still the seven
   *  defaults; per-tab annotations are a future-slice concern. */
  activeInstallMethod?: InstallMethod;
  /** Profile-extraction state from the parent's `useProfileExtraction`
   *  hook. Surfaced for the "Analyzing your site…" / "Couldn't analyze"
   *  status lines; the actual suggestion ROWS come from
   *  `recommendations` (ADR-0050 §2.3) so the surface stays uniform
   *  whether the LLM is dormant or live. */
  extraction: UseProfileExtractionState;
  /** Merged baseline of detection-driven + (dormant) LLM-driven skill
   *  recommendations (ADR-0050 §2.3). Since the 2026-07-14 merge these
   *  no longer render a separate panel — each recommendation annotates
   *  its card in the single grid with a DETECTED / SILL ANALYZER tag
   *  (rationale in the tooltip) and pre-selects it via the narrowing
   *  effect. Empty/undefined → no tags, all seven stay on (fail-open). */
  recommendations?: readonly SkillRecommendation[];
}

/**
 * Imperative handle so a parent (`SkillsStep`'s "Continue to install"
 * click) can commit a detection-narrowed display to the server as one
 * explicit, click-gated action — see the file-level docblock. Calling
 * this from anywhere OTHER than a genuine merchant-initiated action
 * (e.g. from an effect) would reintroduce the auto-apply violation
 * this whole design works around.
 */
export interface SkillsListHandle {
  commitPendingSkills: () => void;
}

export const SkillsList = forwardRef<SkillsListHandle, SkillsListProps>(
  function SkillsList(
    { activeInstallMethod: _activeInstallMethod, extraction, recommendations },
    ref,
  ): JSX.Element {
  const C = useC();
  const api = useApi();
  const { activeSite } = useSites();
  const [skills, setSkills] = useState<Skill[]>(() =>
    // ADR-0197 / founder 2026-07-28 (vbout.com — a non-store marketing
    // site was shown order-status / track-shipment / browse as active
    // suggestions). NOTHING is pre-selected. The grid starts ALL OFF;
    // skills only light up via the detection-narrow effect below when
    // detection actually recommends them (a store/CMS with a catalog).
    // An undetected or non-commerce site therefore shows zero suggested
    // skills — the merchant enables exactly what they want. (Transactional
    // skills additionally stay locked until a payment path is connected,
    // enforced in the narrow effect + the amber-Lock affordance.)
    DEFAULT_SKILLS.map((s) => ({ ...s, enabled: false })),
  );
  const [previewSkill, setPreviewSkill] = useState<Skill | null>(null);

  // See file-level docblock ("Two accuracy fixes"). `touchedRef` marks
  // the FIRST manual toggle click — after that, detection may never
  // narrow the grid again, full stop, regardless of when it resolves.
  const touchedRef = useRef(false);
  const appliedDetectionDefaultRef = useRef(false);

  const persist = (next: Skill[]): void => {
    setSkills(next);
    if (activeSite !== null) {
      void api.skills.saveForSite(activeSite.site_id, next);
    }
  };

  const toggleSkill = (id: SkillId): void => {
    touchedRef.current = true;
    persist(skills.map((s) => (s.skill_id === id ? { ...s, enabled: !s.enabled } : s)));
  };

  // Narrow the all-on DISPLAY default to the detected recommendation,
  // exactly once, before any merchant click. LOCAL STATE ONLY —
  // `setSkills`, never `persist` — mounting/an automated recommendation
  // arriving must never itself call `saveForSite` (ADR-0040 §4.4
  // no-auto-apply floor, pinned by the "NO-AUTO-APPLY invariant" suite
  // in `ProfileExtraction.test.tsx`; that floor governs ANY
  // recommendation source, not just the LLM). The narrowed set is
  // committed to the server only via `commitPendingSkills` below, which
  // callers gate behind an explicit merchant action (Continue). This
  // still closes the honesty gap the effect exists for: the grid the
  // merchant SEES immediately reflects what was actually detected,
  // per the intro copy's claim, rather than the platform-agnostic
  // all-seven template.
  //
  // `recommendations` starts `undefined` while detection is in flight
  // and (per `recommendSkillsForDetection`) resolves to `[]` on
  // failure/dormant — both cases are a no-op here, which is the
  // fail-open behavior the existing detection-failure test already
  // pins ("the merchant still has a working surface").
  useEffect(() => {
    if (touchedRef.current) return;
    if (appliedDetectionDefaultRef.current) return;
    if (recommendations === undefined || recommendations.length === 0) return;
    appliedDetectionDefaultRef.current = true;
    const recommendedIds = new Set<SkillId>(
      recommendations
        .filter((r) => isClosedSkillId(r.skill_id))
        .map((r) => r.skill_id),
    );
    setSkills((prev) =>
      prev.map((s) => ({
        ...s,
        // Transactional skills stay OFF even when detection recommends
        // them — they are gated on a connected payment path, not on
        // detection (founder 2026-07-27).
        enabled: NEEDS_PAYMENT_SKILLS.has(s.skill_id)
          ? false
          : recommendedIds.has(s.skill_id),
      })),
    );
  }, [recommendations]);

  // Commit a detection-narrowed display that was never otherwise
  // persisted (the merchant made no manual edit) — called by the
  // parent's Continue click, i.e. gated behind an explicit action.
  // A no-op when the merchant already touched anything, since the manual
  // edit path (`toggleSkill`) already persists immediately on its click.
  useImperativeHandle(
    ref,
    () => ({
      commitPendingSkills: (): void => {
        if (touchedRef.current) return;
        if (!appliedDetectionDefaultRef.current) return;
        persist(skills);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skills],
  );

  // Founder 2026-07-14: the separate "Suggested for your site" panel
  // duplicated every skill card — same seven skills rendered twice. It's
  // gone; the recommendation now annotates the single grid. A recommended
  // skill gets a DETECTED / SILL ANALYZER tag (rationale in its tooltip)
  // and is pre-toggled ON by the narrowing effect; the rest render OFF.
  const recBySkill = new Map<SkillId, SkillRecommendation>();
  (recommendations ?? []).forEach((r) => {
    if (isClosedSkillId(r.skill_id)) recBySkill.set(r.skill_id, r);
  });
  const analyzing = extraction.status === 'running';

  return (
    <div>
      <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
          }}
        >
          SKILLS THIS UNLOCKS
        </span>
        {analyzing ? (
          <span
            className="flex items-center gap-1.5"
            style={{ fontFamily: sansFont, fontSize: 11, color: C.muted }}
            data-testid="profile-extraction-running"
          >
            <RefreshCw
              size={10}
              style={{ animation: 'spin 1.4s linear infinite' }}
            />
            Analyzing…
          </span>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {skills.map((s, i) => {
          const Icon = iconFor(s.skill_id);
          const rec = recBySkill.get(s.skill_id);
          return (
            <motion.div
              key={s.skill_id}
              data-testid={`skill-card-${s.skill_id}`}
              data-recommended={rec !== undefined ? 'true' : 'false'}
              data-enabled={s.enabled ? 'true' : 'false'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => setPreviewSkill(s)}
              className="p-4 cursor-pointer"
              style={{
                background: C.surface,
                border: `1px solid ${s.enabled ? C.border : C.borderSoft}`,
                borderRadius: 3,
                opacity: s.enabled ? 1 : 0.55,
              }}
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 30,
                    height: 30,
                    background: C.surface2,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                  }}
                >
                  <Icon size={14} color={C.text} strokeWidth={1.5} />
                </div>
                <div
                  className="flex items-center gap-1.5 flex-wrap justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  {rec !== undefined ? (
                    <Pill tone="lime" icon={Check}>
                      {rec.source === 'llm' ? 'SILL ANALYZER' : 'DETECTED'}
                    </Pill>
                  ) : null}
                  {NEEDS_PAYMENT_SKILLS.has(s.skill_id) && !s.enabled ? (
                    <Pill tone="amber">NEEDS PAYMENT</Pill>
                  ) : null}
                  {s.auth_required && (
                    <Tooltip content="This skill requires a cryptographically signed mandate before executing — protects you from unauthorized agent actions.">
                      <Pill tone="amber" icon={Lock}>
                        SIGNED
                      </Pill>
                    </Tooltip>
                  )}
                  <Toggle
                    on={s.enabled}
                    onChange={() => toggleSkill(s.skill_id)}
                    disabled={NEEDS_PAYMENT_SKILLS.has(s.skill_id)}
                    aria-label={`Toggle ${s.name}`}
                  />
                </div>
              </div>
              {/* Human name + a per-skill (i) tooltip (founder: no raw
                  `skill_id`, and the hover must be unique + informative). */}
              <div className="flex items-center gap-1.5" style={{ marginBottom: 4 }}>
                <span
                  style={{
                    fontFamily: sansFont,
                    fontSize: 14,
                    fontWeight: 500,
                    color: C.text,
                  }}
                >
                  {s.name}
                </span>
                <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
                  <Tooltip
                    content={tooltipFor(s.skill_id)}
                    data-testid={`skill-info-${s.skill_id}`}
                  >
                    <Info size={13} style={{ color: C.muted, cursor: 'help', display: 'block' }} />
                  </Tooltip>
                </span>
              </div>
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.muted,
                  lineHeight: 1.5,
                }}
              >
                {s.description}
              </div>
              {NEEDS_PAYMENT_SKILLS.has(s.skill_id) ? (
                <div
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: C.amberFg,
                    lineHeight: 1.5,
                    marginTop: 6,
                  }}
                >
                  Connect a payment path (Stripe or Shopify) to enable this.
                </div>
              ) : null}
            </motion.div>
          );
        })}
      </div>

      <div
        className="px-4 py-3"
        style={{
          border: `1px dashed ${C.border}`,
          borderRadius: 2,
          fontFamily: sansFont,
          fontSize: 12,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        Need something else? Add a custom skill in Settings once your domain is
        verified.
      </div>

      <SkillPreviewModal skill={previewSkill} onClose={() => setPreviewSkill(null)} />
    </div>
  );
  },
);
